import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { COMMUNICATOR_ROLES, ZOOM_ROLES } from '../Palette/paletteRoles';

/**
 * ТЗ 0104, §3.10 — кто что видит из ИИ-агента ERP. Зеркало ролей бэкенда
 * (`ai_agents`): меню, маршрут, палитра и встроенные блоки берут списки
 * ТОЛЬКО отсюда — места не имеют права разойтись. Скрытие — UX, авторитет у
 * сервера: каждый резолвер ИИ-артефакта сам проверяет роль и видимость
 * субъекта (студента, треда, занятия).
 */

/** Страница `/ai-agent`: прогоны, бюджет, чат, база знаний — только Admin. */
export const AI_AGENT_ROLES: UserRole[] = [UserRole.ADMIN];

/**
 * Находки наблюдателя Slack и «Nie to» — управленческие роли, как у задач
 * (`MANAGEMENT_ROLES` бэкенда: Admin, AM, Sales, HR, Finance).
 */
export const AI_FINDING_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
  UserRole.FINANCIER,
];

/**
 * Черновик ответа в коммуникаторе — роли коммуникатора. Та же константа,
 * что у маршрута `/communicator`, а не копия: видимость треда
 * (`thread_blocked`) проверяет сервер.
 */
export const AI_REPLY_ROLES: UserRole[] = COMMUNICATOR_ROLES;

/**
 * Портрет студента — роли карточки студента (Admin, AM, Sales, Finance,
 * Methodist); видимость самого студента — скоупленным менеджером сервера.
 */
export const AI_PORTRAIT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
  UserRole.METHODIST,
];

/** Финансовое приложение портрета — Admin, Finance, Sales, AM. */
export const AI_FINANCE_PORTRAIT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.SALESMANAGER,
  UserRole.ACCOUNTANT,
];

/**
 * Анализ занятий, портрет практик преподавателя, сравнение групп — зеркало
 * `LESSON_ANALYSIS_ROLES` бэкенда (`ai_agents/lessons/services.py`): Admin,
 * Methodist, AccountManager, HRManager — с одинаковыми действиями (запрос и
 * перезапуск разбора, решение по спорному). Решение владельца 09.10 сняло
 * прежний запрет для HR (методология §32.7); портрет СТУДЕНТА — по-прежнему
 * `AI_PORTRAIT_ROLES`, без HR.
 */
export const AI_LESSON_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.METHODIST,
  UserRole.ACCOUNTANT,
  UserRole.HRMANAGER,
];

export interface IAiAgentPermissions {
  /** Страница `/ai-agent` со всеми вкладками. */
  canOpenPage: boolean;
  /** Значок «AI» и «Nie to» / «Potwierdź» у задач из Slack. */
  canSeeFindings: boolean;
  /** Панель черновика над композитором коммуникатора. */
  canUseReplies: boolean;
  /** Портрет в карточке и мини-карточке студента. */
  canSeePortrait: boolean;
  /** Финансовый блок портрета. */
  canSeeFinancePortrait: boolean;
  /** Анализ занятия, чип балла, портрет практик преподавателя. */
  canSeeLessonAnalysis: boolean;
  /**
   * Ссылка «Открыть карточку занятия» (`/zoom/lessons/:id`) из панели
   * разбора — только ролям с маршрутом Zoom (`ZOOM_ROLES`: Admin, AM,
   * Methodist). HR разбор видит, а маршрута Zoom у него нет — ему разбор
   * открывается только боковой панелью, без ссылки в «Не найдено».
   */
  canOpenLessonCard: boolean;
}

const hasAny = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/** Роли без React — для чистых проверок и тестов. */
export const aiAgentPermissionsFor = (
  roles: UserRole[],
): IAiAgentPermissions => ({
  canOpenPage: hasAny(roles, AI_AGENT_ROLES),
  canSeeFindings: hasAny(roles, AI_FINDING_ROLES),
  canUseReplies: hasAny(roles, AI_REPLY_ROLES),
  canSeePortrait: hasAny(roles, AI_PORTRAIT_ROLES),
  canSeeFinancePortrait: hasAny(roles, AI_FINANCE_PORTRAIT_ROLES),
  canSeeLessonAnalysis: hasAny(roles, AI_LESSON_ROLES),
  canOpenLessonCard: hasAny(roles, ZOOM_ROLES),
});

export const useAiAgentPermissions = (): IAiAgentPermissions => {
  const userState = useReactiveVar(userStateVar);
  return aiAgentPermissionsFor((userState.me?.roles ?? []) as UserRole[]);
};
